import {HttpErrorResponse} from '@angular/common/http';
import {Component, OnInit} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {PolymorpheusComponent} from '@taiga-ui/polymorpheus';
import {TuiAlertService, TuiDialogService} from '@taiga-ui/core';

import {EvolutionInstanceInfo} from '@gatewee/types/evolution';
import {GateweeConfirmDialogComponent} from '@gatewee/components/v2';
import {WhatsappService} from '../../_services/communications/whatsapp.service';
import {WhatsappQrCodeDialogComponent} from './dialogs/whatsapp-qr-code-dialog.component';
import {WhatsappTestMessageDialogComponent} from './dialogs/whatsapp-test-message-dialog.component';

@Component({
  selector: 'app-whatsapp-settings',
  templateUrl: './whatsapp-settings.component.html',
  standalone: false,
})
export class WhatsappSettingsComponent implements OnInit {
  instance!: EvolutionInstanceInfo;
  isconnected = false;
  qrCode: string | undefined;

  breadcrumb = [
    {label: 'nav.breadcrumb.settings', path: '/panel'},
    {
      label:
        'features.settings.parameters.enabledCommunications.whatsapp.label',
    },
  ];

  constructor(
    private readonly dialogService: TuiDialogService,
    private readonly alerts: TuiAlertService,
    private readonly whatsappService: WhatsappService,
    private readonly translate: TranslateService,
  ) {}

  ngOnInit(): void {
    this.whatsappService.getWhatsapp().subscribe({
      next: (res: EvolutionInstanceInfo) => {
        this.instance = res;
        this.isconnected = res?.connectionStatus === 'open';

        if (this.instance) {
          this.instance.ownerJid = res?.ownerJid?.split('@')[0];
        } else {
          this.manageConnection();
        }
      },
      error: (err: HttpErrorResponse) => {
        console.error(err);
      },
    });
  }

  manageConnection(): void {
    this.dialogService
      .open<boolean>(new PolymorpheusComponent(GateweeConfirmDialogComponent), {
        data: {
          title: this.isconnected
            ? this.translate.instant(
                'features.communications.messagesAlert.disconnectedWhatsapp',
              )
            : this.translate.instant(
                'features.communications.messagesAlert.connectedWhatsapp',
              ),
          message: this.isconnected
            ? this.translate.instant(
                'features.communications.messagesAlert.askDisconnect',
              )
            : this.translate.instant(
                'features.communications.messagesAlert.askConnect',
              ),
          confirmLabel: this.translate.instant(
            'features.communications.messagesAlert.next',
          ),
          cancelLabel: this.translate.instant(
            'features.communications.messagesAlert.cancel',
          ),
        },
        size: 'm',
        dismissible: true,
      })
      .subscribe(confirmed => {
        if (confirmed) {
          if (this.isconnected) {
            this.disconnect();
          } else {
            this.connect();
          }
        }
      });
  }

  disconnect(): void {
    this.whatsappService.disconnectInstance().subscribe({
      next: () => {
        this.isconnected = false;
      },
      error: (err: HttpErrorResponse) => {
        console.error('Error disconnecting:', err);
        this.alerts
          .open(
            err?.error?.message ??
              this.translate.instant(
                'features.communications.messagesAlert.messageError',
              ),
            {
              label: this.translate.instant(
                'features.communications.messagesAlert.titleError',
              ),
              appearance: 'error',
            },
          )
          .subscribe();
      },
    });
  }

  connect(): void {
    this.whatsappService.connectWhatsapp().subscribe({
      next: (res: unknown) => {
        this.isconnected = true;
        const body = res as {base64?: string; qrcode?: {base64: string}};
        this.qrCode = body?.base64 ?? body?.qrcode?.base64;
        if (this.qrCode) {
          this.dialogService
            .open(new PolymorpheusComponent(WhatsappQrCodeDialogComponent), {
              data: {qrCode: this.qrCode},
              size: 'm',
              dismissible: true,
            })
            .subscribe();
        }
      },
      error: (err: HttpErrorResponse) => {
        console.error('Error connecting:', err);
        this.alerts
          .open(
            this.translate.instant(
              'features.communications.messagesAlert.messageConnectError',
            ),
            {
              label: this.translate.instant(
                'features.communications.messagesAlert.titleError',
              ),
              appearance: 'error',
            },
          )
          .subscribe();
      },
    });
  }

  openTestMessageDialog(): void {
    this.dialogService
      .open<string | null>(
        new PolymorpheusComponent(WhatsappTestMessageDialogComponent),
        {size: 'm', dismissible: true},
      )
      .subscribe(phone => {
        if (phone !== null && phone !== '') {
          this.sendTestMessage(phone);
        }
      });
  }

  sendTestMessage(phone: string): void {
    const digits = typeof phone === 'string' ? phone.replace(/\D/g, '') : '';
    if (!digits) {
      this.alerts
        .open(
          this.translate.instant(
            'features.communications.messagesAlert.messageTest',
          ),
          {
            label: this.translate.instant(
              'features.communications.messagesAlert.titleError',
            ),
            appearance: 'error',
          },
        )
        .subscribe();
      return;
    }

    this.whatsappService.sendTestMessage(digits).subscribe({
      next: () => {
        this.alerts
          .open(
            this.translate.instant(
              'features.communications.messagesAlert.messageSuccess',
            ),
            {
              label: this.translate.instant(
                'features.communications.messagesAlert.titleSuccess',
              ),
              appearance: 'success',
            },
          )
          .subscribe();
      },
      error: (err: HttpErrorResponse) => {
        console.error('Error sending test message:', err);
        this.alerts
          .open(
            this.translate.instant(
              'features.communications.messagesAlert.messageError',
            ),
            {
              label: this.translate.instant(
                'features.communications.messagesAlert.titleError',
              ),
              appearance: 'error',
            },
          )
          .subscribe();
      },
    });
  }
}
